import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { pick } from 'lodash';
import { shallow } from 'zustand/shallow';

import { ShareTemplate } from '../../components/Templates';
import { NOT_APPLICABLE_STRING } from '../../constants/general';
import { Icons, IconType } from '../../constants/icons';
import { useShareData } from '../../context';
import { UserInvitationType } from '../../models/UserStore/Invitation';
import { useSpaceStore } from '../../stores/SpaceStore';
import { useSystemStore } from '../../stores/SystemStore';
import { useUserStore } from '../../stores/UserStore';
import { openAppStoreLink } from '../../utils/url';

import { ClassroomSharingScreen } from './ShareType/ClassroomSharingScreen';
import { GameSetSharingScreen } from './ShareType/GameSetSharingScreen';
import { GameSharingScreen } from './ShareType/GameSharingScreen';
import { MultiplayerGameSharingScreen } from './ShareType/MultiplayerGameSharingScreen';
import { PostSharingScreen } from './ShareType/PostSharingScreen';
import { ReferralSharingScreen } from './ShareType/ReferralSharingScreen';
import { UserSharingScreen } from './ShareType/UserSharingScreen';

const { fetchInvitationLink } = useUserStore.getState();
const { fetchStatus } = useSystemStore.getState();

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const ShareScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const query = useQuery();
  const invitationLinkId = query.get('id') ?? NOT_APPLICABLE_STRING;

  const { setSharedData } = useShareData();

  const location = useLocation();
  useEffect(() => {
    setSharedData({
      pathName: location.pathname,
    });
  }, [location.pathname, setSharedData]);

  useEffect(() => {
    fetchStatus().catch(console.error);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchInvitationLink(invitationLinkId)
      .then(() => {
        // Handle successful fetching if needed
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [invitationLinkId]);

  const invitationLink = useUserStore((store) =>
    store.getInvitationLink(invitationLinkId),
  );

  const contentInviterUserId = invitationLink?.userId ?? NOT_APPLICABLE_STRING;
  const {
    name: contentInviterName,
    avatar: contentInviterAvatar,
    profilePicture: contentInviterProfilePicture,
    statistics: contentInviterStatistics,
  } = useUserStore((state) => {
    const profile = state.getProfile(contentInviterUserId);
    return {
      ...pick(profile, ['name', 'avatar', 'profilePicture', 'statistics']),
    };
  }, shallow);

  // Referral Quota Left
  const systemStatus = useSystemStore((store) => store.getStatus());
  const referralQuotaLeft = useMemo(() => {
    const managedInvitationExtraLimit =
      contentInviterStatistics?.managedInvitationExtraLimit ?? 0;
    const managedInvitationCount =
      contentInviterStatistics?.managedInvitationCount ?? 0;
    const exclusiveInvitationCount =
      contentInviterStatistics?.exclusiveInvitationCount ?? 0;
    const exclusiveInvitationLimit =
      contentInviterStatistics?.exclusiveInvitationLimit ?? 0;
    const globalShareCodeLimit = systemStatus?.managedInvitationLimit ?? 3;
    const ans =
      globalShareCodeLimit +
      managedInvitationExtraLimit -
      managedInvitationCount +
      exclusiveInvitationLimit -
      exclusiveInvitationCount;
    return ans;
  }, [
    contentInviterStatistics?.exclusiveInvitationCount,
    contentInviterStatistics?.exclusiveInvitationLimit,
    contentInviterStatistics?.managedInvitationCount,
    contentInviterStatistics?.managedInvitationExtraLimit,
    systemStatus?.managedInvitationLimit,
  ]);

  // leaderboard
  const spaceTeamId =
    invitationLink?.type == UserInvitationType.LeaderboardTeamInvitation &&
    'teamId' in invitationLink.content
      ? invitationLink.content.teamId
      : NOT_APPLICABLE_STRING;

  const { icon: spaceTeamIcon, name: spaceTeamName } = useSpaceStore(
    (store) => {
      const spaceTeam = store.getTeam(spaceTeamId);

      return {
        icon: spaceTeam?.icon.publicUrls?.small,
        name: spaceTeam?.name,
      };
    },
  );
  const spaceTeamClassroomId =
    invitationLink?.type == UserInvitationType.LeaderboardTeamInvitation &&
    'classroomId' in invitationLink.content
      ? invitationLink.content.classroomId
      : NOT_APPLICABLE_STRING;

  const { name: spaceTeamClassroomName, icon: spaceTeamClassroomIcon } =
    useSpaceStore((store) => {
      const spaceTeamClassroom = store.getSpace(spaceTeamClassroomId);

      return {
        name: spaceTeamClassroom?.i18nClassroomInfo.resources[
          spaceTeamClassroom?.i18nClassroomInfo.defaultLocale
        ]?.name,
        icon: spaceTeamClassroom?.icon?.publicUrls?.small,
      };
    }, shallow);
  const leaderboardId =
    invitationLink?.type == UserInvitationType.LeaderboardTeamInvitation &&
    'leaderboardId' in invitationLink.content
      ? invitationLink.content.leaderboardId
      : NOT_APPLICABLE_STRING;

  const {
    name: leaderboardName,
    description: leaderboardDescription,
    endedAt: leaderboardEndedAt,
    memberCount: leaderboardMemberCount,
    rewardOfferCount: leaderboardRewardOfferCount,
    albumCount: leaderboardAlbumCount,
  } = useSpaceStore((store) => {
    const leaderboard = store.getLeaderboard(leaderboardId);
    return {
      name: leaderboard?.i18nLeaderboardInfo.resources[
        leaderboard?.i18nLeaderboardInfo.defaultLocale
      ]?.name,
      description:
        leaderboard?.i18nLeaderboardInfo.resources[
          leaderboard?.i18nLeaderboardInfo.defaultLocale
        ]?.description,
      endedAt: leaderboard?.endedAt,
      memberCount: leaderboard?.statistics.memberCount,
      rewardOfferCount: leaderboard?.statistics.rewardOfferCount,
      albumCount: leaderboard?.statistics.gameSetCount,
    };
  }, shallow);

  // const handleDirectOpenApp = useDeepLink(
  //   `joyajoyapp://user-invite?invitationLinkId=${invitationLinkId}`,
  // );

  const handlePrimaryClick = (type: UserInvitationType) => {
    if (type === UserInvitationType.LeaderboardTeamInvitation) {
      return navigate('/share/join-leaderboard-team', {
        state: { invitationLinkId },
      });
    }
    if (
      [
        UserInvitationType.FriendChallengeInvitation,
        UserInvitationType.ClassroomSharing,
        UserInvitationType.PostSharing,
        UserInvitationType.UserSharing,
        UserInvitationType.GameSetSharing,
        UserInvitationType.GameSharing,
        UserInvitationType.MultiplayerGameSessionInvitation,
      ].includes(type)
    ) {
      openAppStoreLink();
    }
  };

  const isInvitationLinkExpired =
    invitationLink && invitationLink.expiredAt.getTime() < Date.now();

  const renderContentByType = (type: UserInvitationType) => {
    if (!invitationLink || isInvitationLinkExpired) return null;

    switch (type) {
      case UserInvitationType.ClassroomSharing:
        return <ClassroomSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.GameSharing:
      case UserInvitationType.FriendChallengeInvitation:
        return <GameSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.GameSetSharing:
        return <GameSetSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.PostSharing:
        return <PostSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.MultiplayerGameSharing:
      case UserInvitationType.MultiplayerGameSessionInvitation:
        return <MultiplayerGameSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.UserSharing:
        return <UserSharingScreen invitationLink={invitationLink} />;
      case UserInvitationType.Referral:
        return <ReferralSharingScreen invitationLink={invitationLink} />;
      default:
        return (
          <ShareTemplate
            type={invitationLink.type}
            contentInviter={{
              userName: contentInviterName,
              userId: contentInviterUserId,
              userAvatar: contentInviterAvatar,
              userProfilePicture: contentInviterProfilePicture,
            }}
            leaderboardTeamInvitationData={{
              team: { teamIcon: spaceTeamIcon, teamName: spaceTeamName },
              leaderboard: {
                name: leaderboardName,
                description: leaderboardDescription,
                endedAt: leaderboardEndedAt,
                memberCount: leaderboardMemberCount,
                rewardOfferCount: leaderboardRewardOfferCount,
                albumCount: leaderboardAlbumCount,
              },
              space: {
                spaceIcon: spaceTeamClassroomIcon,
                spaceName: spaceTeamClassroomName,
              },
            }}
            referralQuotaLeft={referralQuotaLeft}
            expiryAt={invitationLink.expiredAt}
            onClickPrimary={() => handlePrimaryClick(invitationLink.type)}
          />
        );
    }
  };

  if (isLoading)
    return (
      <>
        <div
          className="flex flex-col justify-center items-center"
          style={{ flex: 1 }}
        >
          <Spinner
            animation="border"
            role="status"
            style={{ color: '#05656B' }}
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </>
    );

  if (!invitationLink || isInvitationLinkExpired) {
    return (
      <>
        <div
          className="flex flex-col items-center justify-center"
          style={{ flex: 1 }}
        >
          <div
            className="flex flex-col gap-1 bg-gray-150"
            style={{
              padding: '1rem',
              margin: '1rem',
              borderRadius: '1rem',
              alignSelf: 'center',
            }}
          >
            <div className="flex items-center gap-1">
              <img
                className="size-icon-m"
                src={Icons[IconType.ReportProblem]}
              />
              <p className="text-md">{t('Expired or Invalid Link')}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return <>{renderContentByType(invitationLink.type)}</>;
};
